const REJECTED_ANCESTOR_TAGS = new Set(["ins", "moveTo"]);
const TEXT_TAGS = new Set(["t", "delText"]);

export type OriginalViewShape =
    | "none"
    | "unreadable"
    | "deletion"
    | "addition"
    | "substitution"
    | "mixed"
    | "reorder";

export type OriginalViewVerdict = {
    ok: boolean;
    shape: OriginalViewShape;
    lost: string[];
    added: string[];
    detail: string;
};

const PASSED: OriginalViewVerdict = { ok: true, shape: "none", lost: [], added: [], detail: "" };

const localName = (node: Element): string => node.nodeName.replace(/^.*:/, "");

const isRejectedByAncestor = (node: Element): boolean => {
    for (let parent = node.parentElement; parent; parent = parent.parentElement) {
        if (REJECTED_ANCESTOR_TAGS.has(localName(parent))) return true;
    }
    return false;
};

const normalize = (text: string): string => text.replace(/\s+/g, " ").trim();

const findBody = (ooxml: string): Element | null => {
    const parsed = new DOMParser().parseFromString(ooxml, "application/xml");
    if (parsed.getElementsByTagName("parsererror").length > 0) return null;

    const bodies = Array.from(parsed.getElementsByTagName("*")).filter((el) => localName(el) === "body");
    if (bodies.length === 0) return null;

    const documentPart = bodies.find((body) => {
        for (let parent = body.parentElement; parent; parent = parent.parentElement) {
            const name = parent.getAttribute("pkg:name") ?? parent.getAttribute("name");
            if (name?.endsWith("/document.xml")) return true;
        }
        return false;
    });
    return documentPart ?? bodies[0];
};

export const originalViewParagraphs = (ooxml: string): string[] | null => {
    const body = findBody(ooxml);
    if (!body) return null;

    const paragraphs = Array.from(body.getElementsByTagName("*")).filter((el) => localName(el) === "p");
    return paragraphs
        .map((paragraph) =>
            normalize(
                Array.from(paragraph.getElementsByTagName("*"))
                    .filter((el) => TEXT_TAGS.has(localName(el)) && !isRejectedByAncestor(el))
                    .map((el) => el.textContent ?? "")
                    .join(""),
            ),
        )
        .filter((text) => text.length > 0);
};

export const originalViewText = (ooxml: string): string => (originalViewParagraphs(ooxml) ?? []).join(" ");

const multisetDifference = (from: string[], against: string[]): string[] => {
    const remaining = new Map<string, number>();
    for (const text of against) remaining.set(text, (remaining.get(text) ?? 0) + 1);

    const missing: string[] = [];
    for (const text of from) {
        const count = remaining.get(text) ?? 0;
        if (count > 0) {
            remaining.set(text, count - 1);
        } else {
            missing.push(text);
        }
    }
    return missing;
};

const differenceShape = (lost: string[], added: string[]): OriginalViewShape => {
    if (lost.length === 0 && added.length === 0) return "reorder";
    if (added.length === 0) return "deletion";
    if (lost.length === 0) return "addition";
    if (lost.length === added.length) return "substitution";
    return "mixed";
};

export const compareOriginalView = (currentOoxml: string, incomingOoxml: string): OriginalViewVerdict => {
    const current = originalViewParagraphs(currentOoxml);
    const incoming = originalViewParagraphs(incomingOoxml);

    if (current === null || incoming === null) {
        const side = current === null ? "the range being replaced" : "the replacement content";
        return {
            ok: false,
            shape: "unreadable",
            lost: [],
            added: [],
            detail: `${side} could not be read as OOXML, so the write cannot be verified`,
        };
    }

    if (current.length === incoming.length && current.every((text, index) => text === incoming[index])) {
        return PASSED;
    }

    const lost = multisetDifference(current, incoming);
    const added = multisetDifference(incoming, current);

    const detail = lost.length > 0
        ? `${lost.length} paragraph(s) would leave the document with no tracked change`
        : added.length > 0
            ? `${added.length} paragraph(s) would be added without being marked as an insertion`
            : "the document's original text would be reordered without any tracked change";

    return { ok: false, shape: differenceShape(lost, added), lost, added, detail };
};
